import { useEffect, useRef, useState } from 'react';
import type { DebugVariable, MemoryAccess, MemoryRegion } from '../lib/debug/types';
import { hexAddress, memoryAccessFlags, parseAddress } from '../lib/memory';
import type { MemoryPage } from '../lib/protocol';
import { Icon } from './Icon';

export function MemoryView({
	memory,
	onInspect,
	mode = 'finished',
	region,
	onSelectRegion,
	debugVariables,
	highlightedVariable,
	onHoverVariable,
	accesses = [],
	accessesTruncated = false,
}: {
	memory: MemoryPage | null;
	onInspect: (offset: number, region?: MemoryRegion | null) => void;
	mode?: 'paused' | 'running' | 'finished';
	region: MemoryRegion | null;
	onSelectRegion: (region: MemoryRegion | null) => void;
	debugVariables: DebugVariable[];
	highlightedVariable?: DebugVariable;
	onHoverVariable: (key: string | null) => void;
	accesses?: MemoryAccess[];
	accessesTruncated?: boolean;
}) {
	const [address, setAddress] = useState('0x00000400');
	const [selected, setSelected] = useState(0);
	const [error, setError] = useState('');
	const regionSummary = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (region) regionSummary.current?.scrollIntoView({ block: 'nearest' });
	}, [region]);
	useEffect(() => {
		if (
			region &&
			memory &&
			region.address >= memory.offset &&
			region.address < memory.offset + memory.bytes.length
		)
			setSelected(region.address - memory.offset);
	}, [region, memory]);
	const previous = useRef<MemoryPage | null>(null);
	const [changed, setChanged] = useState<Set<number>>(new Set());
	// biome-ignore lint/correctness/useExhaustiveDependencies: region is intentionally untracked; re-running on region changes would clear byte-change highlighting
	useEffect(() => {
		const before = previous.current;
		const changes = new Set<number>();
		if (memory) {
			setAddress(hexAddress(memory.offset));
			setError('');
			if (before?.offset !== memory.offset)
				setSelected(
					region &&
						region.address >= memory.offset &&
						region.address < memory.offset + memory.bytes.length
						? region.address - memory.offset
						: 0,
				);
			else
				memory.bytes.forEach((byte, index) => {
					if (byte !== before.bytes[index]) changes.add(index);
				});
		}
		setChanged(changes);
		previous.current = memory;
	}, [memory]);
	const bytes = memory?.bytes;
	const data = bytes ? new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength) : null;
	const hasWord = !!bytes && selected + 4 <= bytes.length;
	const accessFlags =
		memory && mode !== 'finished'
			? memoryAccessFlags(accesses, memory.offset, memory.bytes.length)
			: new Uint8Array(0);
	const visibleLinkedBytes =
		highlightedVariable && memory
			? Math.max(
					0,
					Math.min(
						highlightedVariable.address + highlightedVariable.byteSize,
						memory.offset + memory.bytes.length,
					) - Math.max(highlightedVariable.address, memory.offset),
				)
			: 0;
	const visibleRegionBytes =
		region && memory
			? Math.max(
					0,
					Math.min(region.address + region.byteSize, memory.offset + memory.bytes.length) -
						Math.max(region.address, memory.offset),
				)
			: 0;

	return (
		<section className="panel memory-panel" aria-label="Memory inspector">
			<div className="panel-heading">
				<h2>
					<Icon name="memory" size={14} /> Memory
				</h2>
			</div>
			<div className="memory-intro">
				<p>
					Real WebAssembly linear memory. <strong>32-bit · little-endian</strong>
				</p>
				<p className="muted">
					{mode === 'paused'
						? 'Live memory at the current checkpoint.'
						: mode === 'running'
							? 'Last checkpoint snapshot. Pause to inspect an address.'
							: 'Post-execution snapshot, not live variable inspection.'}
				</p>
			</div>
			<form
				className="address-bar"
				onSubmit={(event) => {
					event.preventDefault();
					const offset = parseAddress(address);
					if (offset === null || !memory || offset >= memory.totalBytes) {
						setError('Enter an address within the allocated memory.');
						return;
					}
					setError('');
					onInspect(offset);
				}}
			>
				<label htmlFor="memory-address">Address</label>
				<input
					id="memory-address"
					value={address}
					onChange={(e) => setAddress(e.target.value)}
					disabled={!memory || mode === 'running'}
				/>
				<button disabled={!memory || mode === 'running'} type="submit">
					Inspect
				</button>
			</form>
			{error && (
				<p className="inline-error" role="alert">
					{error}
				</p>
			)}
			{!memory || !bytes ? (
				<div className="empty-state">
					<span className="empty-symbol" aria-hidden="true">
						[ 00 ]
					</span>
					<h3>A closer look at your program</h3>
					<p>
						Run some C to inspect its actual bytes. Select a byte to interpret it as an integer or
						float.
					</p>
				</div>
			) : (
				<>
					{region && (
						<div
							className="memory-region-summary"
							ref={regionSummary}
							role="note"
							aria-label="Selected memory region"
						>
							<strong>{region.label ?? 'Selected region'}</strong>
							<span>
								{hexAddress(region.address)}–{hexAddress(region.address + region.byteSize - 1)} ·{' '}
								{region.byteSize} bytes
							</span>
							<span className="muted">
								{visibleRegionBytes} of {region.byteSize} bytes visible
								{visibleRegionBytes < region.byteSize
									? ' · use Previous / Next to inspect the rest'
									: ''}
							</span>
							<button type="button" onClick={() => onSelectRegion(null)}>
								Clear region
							</button>
						</div>
					)}
					{mode !== 'finished' && (
						<div className="memory-access-legend">
							<div>
								<span className="access-read">Read</span>
								<span className="access-write">Written</span>
								<span className="access-both">Read + written</span>
								<span className="access-changed">Value changed</span>
							</div>
							<p>
								Accesses since the previous pause{mode === 'running' ? ' (last snapshot)' : ''}.
								Compiler loads/stores only; library internals and bulk copies are not tracked.
								Outlined bytes mark selected storage.
							</p>
							{accessesTruncated && (
								<p role="status">Access display capped at 2,048 distinct ranges for this step.</p>
							)}
						</div>
					)}
					<div className="memory-meta">
						<span>{(memory.totalBytes / 1024).toLocaleString()} KiB allocated</span>
						<span>{bytes.length} bytes shown</span>
					</div>
					{mode === 'paused' && (
						<div className="memory-hover-summary" role="note" aria-label="Linked variable storage">
							{highlightedVariable
								? `${highlightedVariable.name} · ${hexAddress(highlightedVariable.address)} · ${highlightedVariable.byteSize} bytes${visibleLinkedBytes < highlightedVariable.byteSize ? ` (${visibleLinkedBytes} visible)` : ''}`
								: 'Hover a variable or its bytes to link code, values, and storage.'}
						</div>
					)}
					{/* biome-ignore lint/a11y/noStaticElementInteractions: hover-clearing only; no activation action */}
					<div className="hex-scroll" onMouseLeave={() => onHoverVariable(null)}>
						<div className="hex-row hex-labels">
							<span>ADDRESS</span>
							<span>HEX</span>
							<span>ASCII</span>
						</div>
						{Array.from({ length: Math.ceil(bytes.length / 16) }, (_, row) => {
							const rowBytes = bytes.slice(row * 16, row * 16 + 16);
							return (
								// biome-ignore lint/suspicious/noArrayIndexKey: grid cells are positional; the row index is the cell identity
								<div className="hex-row" key={row}>
									<span className="hex-address">{hexAddress(memory.offset + row * 16)}</span>
									<div className="hex-bytes">
										{Array.from(rowBytes, (byte, column) => {
											const index = row * 16 + column;
											const at = memory.offset + index;
											const inRegion =
												!!region && at >= region.address && at < region.address + region.byteSize;
											const linked =
												!!highlightedVariable &&
												at >= highlightedVariable.address &&
												at < highlightedVariable.address + highlightedVariable.byteSize;
											const read = !!(accessFlags[index] & 1);
											const written = !!(accessFlags[index] & 2);
											const accessLabel =
												read && written
													? 'Read and written'
													: read
														? 'Read'
														: written
															? 'Written'
															: '';
											return (
												<button
													type="button"
													// biome-ignore lint/suspicious/noArrayIndexKey: grid cells are positional; the column index is the cell identity
													key={column}
													className={`byte ${!region && selected === index ? 'selected' : ''} ${inRegion ? 'byte-region' : ''} ${linked ? 'byte-linked' : ''} ${changed.has(index) ? 'byte-changed' : ''} ${read ? 'byte-read' : ''} ${written ? 'byte-written' : ''}`}
													aria-label={`Byte at ${hexAddress(at)}: ${byte}${accessLabel ? ` · ${accessLabel.toLowerCase()} since previous pause` : ''}${inRegion ? ' · selected storage' : ''}${linked ? ` · ${highlightedVariable.name} storage` : ''}`}
													aria-pressed={inRegion || (!region && selected === index)}
													title={accessLabel ? `${accessLabel} since previous pause` : undefined}
													onMouseLeave={() => onHoverVariable(null)}
													onMouseEnter={() => {
														const target = debugVariables
															.filter(
																(variable) =>
																	at >= variable.address &&
																	at < variable.address + variable.byteSize,
															)
															.sort((a, b) => a.byteSize - b.byteSize)[0];
														onHoverVariable(target?.key ?? null);
													}}
													onClick={() => {
														setSelected(index);
														onSelectRegion(null);
													}}
												>
													{byte.toString(16).padStart(2, '0')}
												</button>
											);
										})}
									</div>
									<span className="hex-ascii">
										{Array.from(rowBytes, (b) =>
											b >= 32 && b <= 126 ? String.fromCharCode(b) : '.',
										).join('')}
									</span>
								</div>
							);
						})}
					</div>
					<div className="memory-pagination">
						<button
							type="button"
							disabled={mode === 'running' || memory.offset === 0}
							onClick={() => onInspect(Math.max(0, memory.offset - 256), region)}
						>
							← Previous
						</button>
						<span>{hexAddress(memory.offset)}</span>
						<button
							type="button"
							disabled={mode === 'running' || memory.offset + bytes.length >= memory.totalBytes}
							onClick={() => onInspect(memory.offset + 256, region)}
						>
							Next →
						</button>
					</div>
					<div className="interpretation">
						<h3>Interpret at {hexAddress(memory.offset + selected)}</h3>
						<dl>
							<div>
								<dt>uint8</dt>
								<dd>{bytes[selected]}</dd>
							</div>
							<div>
								<dt>int32</dt>
								<dd>{hasWord && data ? data.getInt32(selected, true) : '—'}</dd>
							</div>
							<div>
								<dt>float32</dt>
								<dd>{hasWord && data ? data.getFloat32(selected, true).toPrecision(6) : '—'}</dd>
							</div>
						</dl>
						<p className="muted">
							These are byte interpretations, not inferred C types.{' '}
							{mode === 'finished' ? 'Stack locals have ended their lifetimes. ' : ''}Freed and
							uninitialized bytes are not valid object values.
						</p>
					</div>
				</>
			)}
			<div className="roadmap-note">
				<span className="tag">Experimental source debugger</span>
				<p>
					Use Debug for checkpoints, scoped variables, and slow playback. Click the editor gutter to
					set breakpoints. Heap lifetime tracking and complete memory-safety checks are not
					implemented.
				</p>
			</div>
		</section>
	);
}
